import type { EntityType, FlatContentLayersType } from "@config";

export const updateEntityChildrenSettings = (
  entity: EntityType,
  flatContentLayers: FlatContentLayersType,
  settings: Record<string, unknown>,
  childCurrentSettings: Record<string, Record<string, unknown>>,
) => {
  const hasNoAlignmentSettings =
    !settings.hasOwnProperty("horizontalAlignment") &&
    !settings.hasOwnProperty("verticalAlignment");

  if (
    entity.settings.name === "FullScreen" &&
    Object.keys(settings).length === 1 &&
    hasNoAlignmentSettings
  ) {
    updateFullscreenLayout(
      entity,
      flatContentLayers,
      settings,
      childCurrentSettings,
    );
    return;
  }

  if (entity.type === "slide" && hasNoAlignmentSettings) {
    updateSlideSettings(
      entity,
      flatContentLayers,
      settings,
      childCurrentSettings,
    );
    return;
  }

  entity.childIds.forEach((childId) => {
    const child = flatContentLayers[childId];
    if (
      child.settings.name === "RichText" &&
      settings.hasOwnProperty("horizontalAlignment")
    ) {
      updateTextContentAlignment(child.settings);
    }
    if (
      child.settings.name === "CollapsibleText" &&
      settings.hasOwnProperty("horizontalAlignment")
    ) {
      child.settings.titleHorizontalAlignment = settings.horizontalAlignment;
    }
    childCurrentSettings[childId] = {};
    Object.keys(settings).forEach((key) => {
      childCurrentSettings[childId][key] = child.settings[key];
      child.settings[key] = settings[key];
    });
    updateEntityChildrenSettings(
      child,
      flatContentLayers,
      settings,
      childCurrentSettings,
    );
  });
};

const updateTextContentAlignment = (settings: Record<string, unknown>) => {
  const content = settings.content as string;
  if (
    content?.trim().length === 0 ||
    settings.horizontalAlignment === undefined
  ) {
    return;
  }

  const tmpTree = new DOMParser().parseFromString(content, "text/html");
  const els = tmpTree.querySelectorAll('[style*="text-align"]');
  els.forEach((el) => {
    if (el instanceof HTMLElement) {
      el.style.removeProperty("text-align");
    }
  });

  settings.content = tmpTree.body.innerHTML;
};

// =========== TABS PIN DEFAULT ===========
export const updateTabsDefault = (
  id: string,
  flatContentLayers: FlatContentLayersType,
) => {
  const entity = flatContentLayers[id];
  const parent = flatContentLayers[entity.parentId || ""];
  entity.settings.isTabDefault = true;
  parent.childIds.forEach((childId) => {
    if (childId === id) return;
    const child = flatContentLayers[childId];
    child.settings.isTabDefault = false;
  });
};

export const getCurrentRelativeEntitiesSettings = (
  parentId: string,
  flatContentLayers: FlatContentLayersType,
) => {
  return flatContentLayers[parentId].childIds.reduce(
    (acc, childId) => {
      acc[childId] = {
        isTabDefault: flatContentLayers[childId].settings["isTabDefault"],
      };
      return acc;
    },
    {} as Record<string, Record<string, unknown>>,
  );
};
// =========== TABS PIN DEFAULT ===========

// =========== FULLSCREEN LAYOUT ===========
const updateFullscreenLayout = (
  entity: EntityType,
  flatContentLayers: FlatContentLayersType,
  settings: Record<string, unknown>,
  childCurrentSettings: Record<string, Record<string, unknown>>,
) => {
  const allowedKeys = ["layoutStyle", "mediaPosition", "groupsRatio"];
  entity.childIds.forEach((childId) => {
    const set = flatContentLayers[childId];
    childCurrentSettings[childId] = {};
    Object.keys(settings).forEach((key) => {
      if (allowedKeys.includes(key)) {
        childCurrentSettings[childId][key] = set.settings[key];
        set.settings[key] = settings[key];
      }
    });
  });
};

// =========== FULLSCREEN LAYOUT ===========

// =========== SLIDE SETTINGS ===========
const updateSlideSettings = (
  entity: EntityType,
  flatContentLayers: FlatContentLayersType,
  settings: Record<string, unknown>,
  childCurrentSettings: Record<string, Record<string, unknown>>,
) => {
  const allowedKeys = [
    "backgroundColor",
    "backgroundType",
    "overlayOpacity",
    "backgroundImage",
    "backgroundVideo",
  ];
  entity.childIds.forEach((childId) => {
    const combo = flatContentLayers[childId];
    if (combo.settings.name !== "FullScreen") return;
    childCurrentSettings[childId] = {};
    Object.keys(settings).forEach((key) => {
      if (allowedKeys.includes(key)) {
        childCurrentSettings[childId][key] = combo.settings[key];
        combo.settings[key] = settings[key];
      }
    });
  });
};
// =========== SLIDE SETTINGS ===========
